import "./SwapPage.scss"
import { Box } from "@mui/material"
import React, { ReactElement } from "react"
import Container from "src/components/PageContainer"
import { SWAP_TYPES, TOKENS_MAP } from "../constants"
import { BigNumber } from "@ethersproject/bignumber"
import ReviewSwap from "./ReviewSwap"
import SwapInput from "./SwapInput"
import type { TokenOption } from "../pages/Swap"
import { commify, formatBNToPercentString, toSignificant } from "../utils"
import { formatBNToString } from "../utils"
import { logEvent } from "../utils/googleAnalytics"
import { useActiveWeb3React } from "../hooks"
import { useTranslation } from "react-i18next"
import Text from "./Text"
import Web3Status from "./Web3Status"
import SlippageRow from "./SlippageRow"
import Button from "./Button"
import Modal from "./Modal"
import { useBoolean } from "src/hooks/useBoolean"
import { TransactionSummaryData } from "./TransactionSummary"
import { subtractSlippage } from "src/utils/slippage"
import { AppState } from "src/state"
import { useSelector } from "react-redux"

interface Props {
  tokenOptions: {
    from: TokenOption[]
    to: TokenOption[]
  }
  exchangeRateInfo: {
    pair: string
    exchangeRate: BigNumber
    priceImpact: BigNumber
    route: string[]
  }
  txnGasCost: {
    amount: BigNumber
    valueUSD: BigNumber | null // amount * ethPriceUSD
  }
  error: string | null
  swapType: SWAP_TYPES
  fromState: { symbol: string; value: string; valueUSD: BigNumber }
  toState: {
    symbol: string
    value: string
    valueUSD: BigNumber
    rawAmount: BigNumber
    poolName: string
  }
  onChangeFromToken: (tokenSymbol: string) => void
  onChangeFromAmount: (amount: string) => void
  onChangeToAmount: (amount: string) => void
  onChangeToToken: (tokenSymbol: string) => void
  onConfirmTransaction: (summary: TransactionSummaryData) => Promise<void>
  onClickReverseExchangeDirection: () => void
}

const SwapPage = (props: Props): ReactElement => {
  const { t } = useTranslation()
  const {
    tokenOptions,
    exchangeRateInfo,
    fromState,
    toState,
    // txnGasCost,
    error,
    // swapType,
    onChangeFromToken,
    onChangeFromAmount,
    onChangeToAmount,
    onChangeToToken,
    onConfirmTransaction,
    onClickReverseExchangeDirection,
  } = props

  const [showConfirmModal, openConfirmModal, closeConfirmModal] =
    useBoolean(false)

  const { slippageCustom, slippageSelected } = useSelector(
    (state: AppState) => state.user,
  )
  const { account } = useActiveWeb3React()
  const formattedExchangeRate = commify(
    formatBNToString(exchangeRateInfo.exchangeRate, 18, 6),
  )
  const formattedRoute = exchangeRateInfo.route.join(" > ")
  const fromToken = TOKENS_MAP[fromState.symbol]
  const toToken = TOKENS_MAP[toState.symbol]
  const minReceive = commify(
    toSignificant(
      formatBNToString(
        subtractSlippage(toState.rawAmount, slippageSelected, slippageCustom),
        toToken?.decimals || 0,
      ),
      6,
    ),
  )

  const summary = [
    {
      label: t("Pay"),
      amount: commify(fromState.value),
      token: fromToken,
    },
    {
      label: t("Receive (Min)"),
      amount: minReceive,
      token: toToken,
    },
  ]

  return (
    <Container>
      <div className="swapPage">
        <div className="content">
          <div className="swapFormWrapper">
            <div className="swapForm">
              <div className="row center" style={{ marginBottom: "2rem" }}>
                <Text variant="h3" textAlign="center">
                  {t("Swap")}
                </Text>
              </div>
              <div>
                <SwapInput
                  tokens={tokenOptions.from}
                  onSelect={onChangeFromToken}
                  onChangeAmount={onChangeFromAmount}
                  selected={fromState.symbol}
                  inputValue={fromState.value}
                  inputValueUSD={fromState.valueUSD}
                  isSwapFrom={true}
                />
              </div>
              <div className="row center">
                <a
                  className="clickable"
                  onClick={onClickReverseExchangeDirection}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.92308 20L8.92308 4L4 9.92593"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15.0769 4L15.0769 20L20 14.2936"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              </div>
              <div className="row">
                <SwapInput
                  tokens={tokenOptions.to}
                  onSelect={onChangeToToken}
                  selected={toState.symbol}
                  inputValue={toState.value}
                  inputValueUSD={toState.valueUSD}
                  onChangeAmount={onChangeToAmount}
                  isSwapFrom={false}
                />
              </div>
              <div style={{ height: "12px" }}></div>
              <Text
                gradient={
                  exchangeRateInfo.priceImpact.gte(0)
                    ? "gradient1"
                    : "gradient-orange"
                }
                borderRadius="10px"
                py={0.5}
                px={2}
                size="micro"
              >
                {exchangeRateInfo.priceImpact.gte(0)
                  ? t("Bonus: ")
                  : t("Price Impact: ")}
                {exchangeRateInfo.priceImpact.gt(0) ? "+" : ""}
                {formatBNToPercentString(exchangeRateInfo.priceImpact, 18, 4)}
              </Text>
              <div style={{ height: "12px" }}></div>
              <div className="row">
                <Box mb={1}>{t("Receive (Min)")}</Box>
                <span>{`${minReceive} ${toState.symbol}`}</span>
              </div>
              {formattedRoute && (
                <>
                  <SlippageRow />
                  {fromState.symbol && toState.symbol && (
                    <div className="row">
                      <div>
                        <span>{t("Price")}</span>
                      </div>
                      <span className="exchRate">{`${formattedExchangeRate} ${toState.symbol} per 1 ${fromState.symbol}`}</span>
                    </div>
                  )}
                  {toState.poolName && (
                    <div className="row">
                      <div>
                        <span>{t("Trade routed through")}</span>
                      </div>
                      <span className="exchRate">{toState.poolName}</span>
                    </div>
                  )}
                </>
              )}
              <Box mt={3} width="100%">
                {account ? (
                  <Button
                    fullWidth
                    onClick={openConfirmModal}
                    disabled={!!error || +toState.value <= 0}
                  >
                    {error ? t(error) : t("Swap")}
                  </Button>
                ) : (
                  <Web3Status fullWidth />
                )}
              </Box>
            </div>
          </div>
          <Modal
            open={showConfirmModal}
            onClose={closeConfirmModal}
            title={t("Confirm Transaction")}
          >
            <ReviewSwap
              formattedExchangeRate={`${formattedExchangeRate} ${toState.symbol} per 1 ${fromState.symbol}`}
              transactionSummary={summary}
              onConfirm={async (): Promise<void> => {
                closeConfirmModal()
                logEvent("swap", {
                  from: fromState.symbol,
                  to: toState.symbol,
                })
                await onConfirmTransaction(summary)
              }}
            />
          </Modal>
        </div>
      </div>
    </Container>
  )
}

export default SwapPage
